import { useCallback, useMemo } from "react";
import { useEmailTemplateWizard } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/hooks/UseEmailTemplateWizard";
import {
  EMAIL_TEMPLATE_WIZARD_STEPS,
  type EmailTemplateStepKey,
  type TemplateCampaignDetails,
} from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/Template/TemplateEditorConstants";
import type { GeneratedTemplate } from "src/layouts/Engagements/Campaigns/Tabs/EmailCampaign/AiEmail/AiCampaignTypes";
import type { CampaignDraftState } from "src/layouts/Engagements/Campaigns/shared/UseCampaignDraft";
import type { ChatCampaignSetupResult } from "./UseChatCampaignSetup";

export interface ChatTemplateStepSnapshot {
  stepIndex: number;
  stepKey: EmailTemplateStepKey;
  template: GeneratedTemplate | null;
  details: TemplateCampaignDetails;
}

interface UseChatTemplateCampaignWizardParams {
  campaignData: Partial<ChatCampaignSetupResult>;
  onAdvance: (_snapshot: ChatTemplateStepSnapshot) => void;
  onBackToSetup: () => void;
  draftController: CampaignDraftState;
}

const FIRST_WIZARD_STEP_INDEX = EMAIL_TEMPLATE_WIZARD_STEPS.findIndex(
  step => step.key === "senderRecipients"
);

export const useChatTemplateCampaignWizard = ({
  campaignData,
  onAdvance,
  onBackToSetup,
  draftController,
}: UseChatTemplateCampaignWizardParams) => {
  const templateCampaignData = useMemo(
    () => ({
      campaignName: campaignData.campaignName ?? "",
      sendTo: campaignData.sendTo ?? [],
      excludeList: campaignData.excludeList ?? [],
      companyNameEngagements: campaignData.companyName ?? "",
    }),
    [campaignData.campaignName, campaignData.companyName, campaignData.excludeList, campaignData.sendTo]
  );

  const wizardBase = useEmailTemplateWizard(templateCampaignData, {
    initialStepIndex: FIRST_WIZARD_STEP_INDEX,
    persist: false,
    draftController,
  });
  const { stepIndex, stepKey, template, campaignName, subjectLine } = wizardBase;
  const details = useMemo(() => ({ campaignName, subjectLine }), [campaignName, subjectLine]);

  const snapshotBeforeAdvance = useCallback(
    (): ChatTemplateStepSnapshot => ({ stepIndex, stepKey, template, details }),
    [details, stepIndex, stepKey, template]
  );

  const goNext = useCallback(() => {
    if (!wizardBase.canProceed) {
      return;
    }
    const snapshot = snapshotBeforeAdvance();
    wizardBase.goNext();
    onAdvance(snapshot);
  }, [onAdvance, snapshotBeforeAdvance, wizardBase]);

  const applyTemplate = useCallback(
    async (templateId: string) => {
      const snapshot = snapshotBeforeAdvance();
      if (await wizardBase.applyTemplate(templateId)) {
        onAdvance(snapshot);
      }
    },
    [onAdvance, snapshotBeforeAdvance, wizardBase]
  );

  const goBack = useCallback(() => {
    if (stepIndex === FIRST_WIZARD_STEP_INDEX) {
      onBackToSetup();
      return;
    }
    wizardBase.goBack();
  }, [onBackToSetup, stepIndex, wizardBase]);

  const { reset: resetBase } = wizardBase;
  const reset = useCallback(() => resetBase(templateCampaignData), [resetBase, templateCampaignData]);

  return useMemo(
    () => ({ ...wizardBase, goNext, applyTemplate, goBack, reset }),
    [applyTemplate, goBack, goNext, reset, wizardBase]
  );
};
